import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import { Box, IconButton, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TopSection from "../../pages/LT/TopSection";
import { Loader } from "../common/Loader";
import Modal from "../common/modal/Modal";
import ButtonComponent from "../common/button/Button";
import { getTicketSummaryDetailsSlice } from "../../redux/actions/getTicketSummaryDetailsSlice";
import { getTicketSummaryDetailsEndpoint } from "../../common/endpoints";
import "react-toastify/dist/ReactToastify.css";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import dayjs from "dayjs";

const getDisplayValue = (value) => (value === null || value === undefined || value === "" ? "-" : value);

const normalizeKey = (value = "") => String(value).replace(/[^a-z0-9]/gi, "").toLowerCase();

const pickValueByKeys = (row = {}, keys = []) => {
    const rowEntries = Object.entries(row || {});
    for (const candidateKey of keys) {
        const match = rowEntries.find(([rowKey]) => normalizeKey(rowKey) === normalizeKey(candidateKey));
        if (match) {
            return match[1];
        }
    }
    return undefined;
};

const hasDisplayValue = (value) => !(value === null || value === undefined || value === "");

const normalizeForecastEntries = (entries) => {
    if (Array.isArray(entries)) return entries;
    if (typeof entries === "string") {
        try {
            const parsed = JSON.parse(entries);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
};

const formatIsoDate = (value) => {
    if (!value || typeof value !== "string") return "-";
    return value.split("T")[0] || "-";
};

const extractTicketText = (value) => value?.split("id=")[1] || value || "-";

const TicketSummary = () => {
    const dispatch = useDispatch();

    const [summaryData, setSummaryData] = useState([]);
    const [page, setPage] = useState(0);
    const [pageSize] = useState(1000);
    const [totalRows, setTotalRows] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [isActionModalOpen, setIsActionModalOpen] = useState(false);
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [isFilterApplied, setIsFilterApplied] = useState(false);
    const roleDetails = localStorage.getItem("userRole");
    const isSupplier = roleDetails === "Supplier Planner" || roleDetails === "Supplier Admin" || roleDetails === "App Admin";
    const pagination = true;
    const paginationPageSize = 10;
    const paginationPageSizeSelector = [10, 50, 100, 500, 1000];
    const isPlanner = roleDetails === "Global Planner" || roleDetails === "Regional Planner";
    const gridRef = useRef(null);
    const formatCreatedDate = (date) => {
        if (!date || date === "-" || date === "NA") return "-";

        const parsed = dayjs(date);

        return parsed.isValid() ? parsed.format("DD-MMM-YYYY") : date;
    };
    const SELECTED_DATA_FIELDS = [
        { label: "Material Number", keys: ["materialNumber", "material", "tccMaterialNumber", "tcccMaterialNumber", "materialId"] },
        { label: "Parent Vendor", keys: ["parentVendorName", "parentVendor"] },
        { label: "Plant Code", keys: ["plantCode", "plant", "plantId"] },
        { label: "Description", keys: ["description", "materialDescription", "materialDesc"] },
        { label: "Vendor Code", keys: ["vendorCode", "childVendor", "supplierCode", "supplier", "supplierId"] },
        ...(roleDetails === "Supplier Planner" || roleDetails === "Supplier Admin"
            ? [{ label: "Supplier Pin", keys: ["supplierPIN"] }]
            : []),

        ...(roleDetails === "App Admin"
            ? [{ label: "External Material Group", keys: ["scode", "materialGroup", "materialGroupCode"] }]
            : []),
        { label: "Business Category", keys: ["businessCategory", "category"] },
    ]

    const loading = useSelector(
        (state) => state.getTicketSummaryDetailsSlice
    );

    const columnDefs = [
        {
            headerName: "Menu",
            field: "menu",
            valueGetter: (params) => params.data?.menu || "-"
        },
        {
            headerName: "Function",
            field: "function",
            valueGetter: (params) => params.data?.function || "-"
        },
        {
            headerName: "Priority",
            field: "priority",
            valueGetter: (params) => params.data?.priority || "-"
        },
        {
            headerName: "Ticket Number",
            field: "ticketNumber",
            filter: "agSetColumnFilter",
            valueGetter: (params) =>
                params.data?.ticketNumber?.split("id=")[1] ||
                params.data?.ticketNumber ||
                "-",
            cellRenderer: (params) => {
                const ticketText = params.value;

                return roleDetails === "Supplier Planner" || roleDetails === "Supplier Admin" ? (
                    ticketText
                ) : (
                    <a
                        href={params.data.ticketNumber}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: "black",
                            textDecoration: "underline",
                        }}
                    >
                        {ticketText}
                    </a>
                );
            },
        },
        {
            headerName: "Status",
            field: "status",
            valueGetter: (params) => params.data?.status || "-"
        },
        {
            headerName: "Description",
            field: "description",
            valueGetter: (params) => params.data?.description || "-",
            filterParams: {
                valueFormatter: (params) =>
                    params.value?.length > 50
                        ? params.value.substring(0, 50) + "..."
                        : params.value
            }
        },
        {
            headerName: "Plant Code",
            field: "plantCode",
            valueGetter: (params) => {
                const entries = Array.isArray(params.data?.forecastEntries)
                    ? params.data.forecastEntries
                    : [];

                const plantCodes = [
                    ...new Set(
                        entries
                            .map(item => item?.plantCode)
                            .filter(Boolean)
                    )
                ];

                return plantCodes.length ? plantCodes.join(", ") : "-";
            }
        },
        ...(isSupplier
            ? [
                {
                    headerName: "Parent Vendor",
                    field: "parentVendor",
                    valueGetter: (params) => params.data?.parentVendor || "-"
                },
            ]
            : []),
        {
            headerName: "Initiator",
            field: "createdBy",
            valueGetter: (params) => params.data?.createdBy || "-"
        },
        ...(roleDetails === "App Admin"
            ? [
                {
                    headerName: "Requested By",
                    field: "userRole",
                    valueGetter: (params) => params.data?.userRole || "-"
                },
            ]
            : []),
        {
            headerName: "Creation Date",
            field: "createdDate",
            valueFormatter: (params) => formatCreatedDate(params.value),
            filter: "agMultiColumnFilter",
            filterParams: {
                filters: [
                    {
                        filter: "agTextColumnFilter",
                    },
                    {
                        filter: "agSetColumnFilter",
                        filterParams: {
                            valueFormatter: (params) => formatCreatedDate(params.value),
                            keyCreator: (params) => formatCreatedDate(params.value),
                        },
                    },
                ],
            },
        },
        {
            headerName: "Action",
            field: "action",
            sortable: false,
            filter: false,
            resizable: false,
            cellStyle: { justifyContent: "center" },
            cellRenderer: (params) => (
                <Tooltip title="View ticket details">
                    <IconButton
                        size="small"
                        sx={{
                            color: "#000000",
                            backgroundColor: "transparent",
                            border: "none",
                            transition: "background-color 0.2s ease, color 0.2s ease",
                            "&:hover": {
                                color: "#000000",
                                backgroundColor: "rgba(0, 0, 0, 0.08)",
                            },
                        }}
                        onClick={() => {
                            setSelectedTicket(params.data || null);
                            setIsPreviewMode(false);
                            setIsActionModalOpen(true);
                        }}
                    >
                        <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )
        }
    ];

    const onFilterChanged = () => {
        if (gridRef.current?.api) {
            const filterModel = gridRef.current.api.getFilterModel();
            setIsFilterApplied(Object.keys(filterModel).length > 0);
        }
    };

    const closeActionModal = () => {
        setIsActionModalOpen(false);
        setSelectedTicket(null);
        setIsPreviewMode(false);
    };

    const mappedForecastEntries = useMemo(
        () => normalizeForecastEntries(selectedTicket?.forecastEntries),
        [selectedTicket]
    );

    const renderForecastEntriesTable = (entries = []) => {
        if (!Array.isArray(entries) || entries.length === 0) return null;

        const visibleFields = SELECTED_DATA_FIELDS.filter((field) =>
            entries.some((row) => hasDisplayValue(pickValueByKeys(row, field.keys)))
        );
        if (!visibleFields.length) return null;

        return (
            <div style={{ margin: "10px 0px", border: "1px solid #bcbcbc", fontSize: 12 }}>
                <div style={{ maxHeight: "180px", overflow: "auto" }}>
                    <table style={{ borderCollapse: "collapse", width: "100%" }}>
                        <thead>
                            <tr>
                                {visibleFields.map((field) => (
                                    <th
                                        key={field.label}
                                        style={{ minWidth: 140, textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #bcbcbc", background: "#f3f3f3" }}
                                    >
                                        {field.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry, index) => (
                                <tr key={`${pickValueByKeys(entry, ["material", "materialNumber", "tcccMaterialNumber"]) || "mat"}_${index}`}>
                                    {visibleFields.map((field) => (
                                        <td key={`${field.label}_${index}`} style={{ minWidth: 140, padding: "4px 8px", borderBottom: "1px solid #f0f0f0" }}>
                                            {field.label === "Plant Code"
                                                ? (() => {
                                                    const plantCode = pickValueByKeys(entry, ["plantCode", "plant", "plantId"]);
                                                    const plantDescription = pickValueByKeys(entry, ["plantDescription"]);

                                                    if (plantCode && plantDescription) {
                                                        return `${plantCode} | ${plantDescription}`;
                                                    }

                                                    return getDisplayValue(plantCode || plantDescription);
                                                })()
                                                : getDisplayValue(pickValueByKeys(entry, field.keys))
                                            }
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const fetchTicketSummary = async () => {
        try {
            const response = await dispatch(
                getTicketSummaryDetailsSlice({
                    dynamicEndpoint:
                        `${getTicketSummaryDetailsEndpoint}?page=${page}&size=${pageSize}`
                })
            );

            if (response?.meta?.requestStatus === "fulfilled") {
                const data = response?.payload;

                setSummaryData(data?.content || []);
                setTotalRows(data?.totalElements || 0);
                setTotalPages(data?.totalPages || 0);
            } else {
                toast.error("Failed to load ticket summary");
            }
        } catch (error) {
            toast.error("Something went wrong");
        }
    };

    useEffect(() => {
        fetchTicketSummary();
    }, [page]);

    const gridOptions = {
        // onFilterChanged,
        overlayLoadingTemplate: `<span class="ag--overlay-loading-center" >Please wait while data is loading</span>`,
        overlayNoRowsTemplate: `<span class="ag--overlay-no-rows-center" >No Data Available</span>`,
        defaultColDef: {
            autoHeight: true,
            cellStyle: {
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.5,
                display: "flex",
                alignItems: "center",
            },
            valueGetter: (params) => {
                if (!params.data) return "-";

                const field = params.colDef.field;
                const value = params.data[field];

                return value === null || value === undefined || value === "" ? "-" : value;
            },
        }

    }

    return (
        <>
            <TopSection
                labelList={[
                    { title: "Home", path: "/landing-page" },
                    { title: "Ticket Summary" }
                ]}
                hideHeader={true}
                headerName="Ticket Summary"
            />

            {loading?.loading ? (
                <Loader />
            ) : (
                <>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-start",
                            marginBottom: "10px",
                            padding: "4px"
                        }}
                    >
                        <ButtonComponent
                            label="Clear Filters"
                            maxWidth="140px"
                            bgColor={isFilterApplied ? "black" : "#d3d3d3"}
                            color={isFilterApplied ? "white" : "#666"}
                            disabled={!isFilterApplied}
                            onClick={() => {
                                if (gridRef.current?.api) {
                                    gridRef.current.api.setFilterModel(null);
                                    setIsFilterApplied(false);
                                }
                            }}
                        />
                    </div>
                    <div
                        className="ag-theme-quartz"
                        style={{
                            height: 500,
                            width: "100%",
                            overflow: "auto",
                        }}
                    >
                        <AgGridReact
                            ref={gridRef}
                            rowData={summaryData}
                            columnDefs={columnDefs}
                            headerHeight={60}
                            gridOptions={gridOptions}
                            onFilterChanged={onFilterChanged}
                            defaultColDef={{
                                sortable: true,
                                filter: true,
                                resizable: true
                            }}
                            pagination={pagination}
                            paginationPageSize={paginationPageSize}
                            paginationPageSizeSelector={paginationPageSizeSelector}
                        />

                    </div>
                    {/*<div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: "12px",
                            padding: "6px 12px",
                            borderTop: "1px solid #d0d0d0",
                            fontSize: "12.5px",
                            background: "#fff",
                            color: "#444"
                        }}
                    >
                       
                        <span>
                            Page Size: <b>{pageSize}</b>
                        </span>

                        
                        <span>
                            {totalRows === 0 ? 0 : page * pageSize + 1}
                            {" "}to{" "}
                            {Math.min((page + 1) * pageSize, totalRows)}
                            {" "}of{" "}
                            {totalRows}
                        </span>

                        <button
                            disabled={page === 0}
                            onClick={() => setPage((prev) => prev - 1)}
                            style={{
                                border: "1px solid transparent",
                                background: "transparent",
                                cursor: page === 0 ? "not-allowed" : "pointer",
                                color: page === 0 ? "#c0c0c0" : "#555",
                                padding: "2px 4px",
                                borderRadius: "2px"
                            }}
                        >
                            ‹
                        </button>

                        <span>
                            Page <b>{page + 1}</b> of <b>{totalPages}</b>
                        </span>

                        <button
                            disabled={page + 1 >= totalPages}
                            onClick={() => setPage((prev) => prev + 1)}
                            style={{
                                border: "1px solid transparent",
                                background: "transparent",
                                cursor: page + 1 >= totalPages ? "not-allowed" : "pointer",
                                color: page + 1 >= totalPages ? "#c0c0c0" : "#555",
                                padding: "2px 4px",
                                borderRadius: "2px"
                            }}
                        >
                            ›
                        </button>
                    </div>*/}
                </>
            )}

            <Modal
                open={isActionModalOpen}
                handleClose={closeActionModal}
                title={isPreviewMode ? "Preview" : "Ticket Details"}
                fullWidth
                maxWidth="md"
                content={
                    !selectedTicket ? (
                        <div>No ticket data available.</div>
                    ) : (
                        <div>
                            {isPreviewMode && <p style={{ marginTop: 0 }}>Preview</p>}
                            <div style={{ border: "1px solid #bcbcbc", padding: 10 }}>
                                <div style={{ display: "flex", margin: "10px 0px" }}>
                                    <b style={{ width: "25%" }}>Ticket Number</b>
                                    <span style={{ width: "70%" }}>: {extractTicketText(selectedTicket?.ticketNumber)}</span>
                                </div>
                                <div style={{ display: "flex", margin: "10px 0px" }}>
                                    <b style={{ width: "25%" }}>Menu</b>
                                    <span style={{ width: "70%" }}>: {getDisplayValue(selectedTicket?.menu)}</span>
                                </div>
                                <div style={{ display: "flex", margin: "10px 0px" }}>
                                    <b style={{ width: "25%" }}>Function</b>
                                    <span style={{ width: "70%" }}>: {getDisplayValue(selectedTicket?.function)}</span>
                                </div>
                                <div style={{ display: "flex", margin: "10px 0px" }}>
                                    <b style={{ width: "25%" }}>Priority</b>
                                    <span style={{ width: "70%" }}>: {getDisplayValue(selectedTicket?.priority)}</span>
                                </div>
                                <div style={{ display: "flex", margin: "10px 0px" }}>
                                    <b style={{ width: "25%" }}>Initiator</b>
                                    <span style={{ width: "70%" }}>: {getDisplayValue(selectedTicket?.createdBy)}</span>
                                </div>
                                <div style={{ display: "flex", margin: "10px 0px" }}>
                                    <b style={{ width: "25%" }}>Status</b>
                                    <span style={{ width: "70%" }}>: {getDisplayValue(selectedTicket?.status)}</span>
                                </div>
                                {isSupplier && (
                                    <div style={{ display: "flex", margin: "10px 0px" }}>
                                        <b style={{ width: "25%" }}>Parent Vendor</b>
                                        <span style={{ width: "70%" }}>: {getDisplayValue(selectedTicket?.parentVendor)}</span>
                                    </div>
                                )}
                                <div style={{ display: "flex", margin: "10px 0px" }}>
                                    <b style={{ width: "25%" }}>Creation Date</b>
                                    <span style={{ width: "70%" }}>: {formatIsoDate(selectedTicket?.createdDate)}</span>
                                </div>
                                <div style={{ display: "flex", margin: "10px 0px" }}>
                                    <b style={{ width: "25%" }}>Issue Description</b>
                                    <span style={{ width: "70%", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>: {getDisplayValue(selectedTicket?.description)}</span>
                                </div>
                                {selectedTicket?.attachmentUrl && (
                                    <div style={{ display: "flex", margin: "10px 0px" }}>
                                        <b style={{ width: "25%" }}>Attachment</b>

                                        <span
                                            style={{
                                                width: "70%",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            :{" "}
                                            <a
                                                href={selectedTicket.attachmentUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    color: "black",
                                                    textDecoration: "underline",
                                                }}
                                            >
                                                {selectedTicket.attachmentFileName}
                                            </a>
                                        </span>
                                    </div>
                                )}
                                {mappedForecastEntries.length > 0 && (
                                    <div style={{ marginTop: 8 }}>
                                        <b>Selected Items</b>
                                        {renderForecastEntriesTable(mappedForecastEntries)}
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                }
                action={
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.2, width: "100%", margin: "10px 0" }}>
                        <>
                            <ButtonComponent
                                maxWidth="120px"
                                label="Close"
                                bgColor="black"
                                color="white"
                                onClick={closeActionModal}
                            />
                        </>

                    </Box>
                }
            />
        </>
    );
};

export default TicketSummary;